/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {
  appIdentifiers,
  pageViewModifiers,
  docViewModes,
  lhpFeatures,
  RHP_VERBS_POSITION,
  flyoutThemes
} from "constants/enums";
import { apiNames } from "constants/apiNames";

const publicApiNames = Object.freeze({
  APP: "app",
  DOCUMENT: "document",
  HUD: "hud",
  LHP: "lhp",
  RHP: "rhp",
  FILLANDSIGN: "fillAndSign",
  QUICKTOOLS: "quickTools",
  LIQUIDMODE: "liquidMode",
  NAVIGATIONBAR: "navigationBar",
  FTE: "FTE",
  STICKYTOOLS: "stickyTools",
  BOTTOM_BOARD: "bottomBoard",
  FAB: "FAB",
  TOP_PANEL: "topPanel",
  FLOATING_WINDOWS: "floatingWindows"
});

const eventNames = Object.freeze({
  OPEN_NATIVE_APP: "handleOpenNativeApp",
  DOCUMENT_OPEN: "documentopen",
  PAGES_IN_VIEW_CHANGE: "pagesinviewchange",
  CURRENT_PAGE_CHANGE: "currentpagechange",
  DOCUMENT_DOWNLOAD: "documentdownload",
  TEXT_SEARCH: "textsearch",
  SEARCH_CLEAR: "searchclear",
  BOOKMARK_ITEM_CLICK: "bookmarkitemclick",
  HYPERLINK_CLICK: "hyperlinkopen",
  TEXT_COPY: "textcopy",
  DOCUMENT_VIEW_UPDATE: "documentviewupdate",
  MODAL_DIALOG_OPEN: "dialogopen",
  MODAL_DIALOG_CLOSE: "dialogclose",
  DOCUMENT_PRINT: "documentprint",
  FALLBACK_PRINT: "fallbackprint",
  PAGE_MOUSE_LEAVE: "pagemouseleave",
  PAGE_MOUSE_ENTER: "pagemouseenter",
  UNSUPPORTED_FEATURE_FOUND: "unsupportedfeaturefound",
  COMPLETE_PDF_BUFFER_AVAILABLE: "completePDFBufferAvailable",
  ERROR_OCCURRED: "errorOccurred",
  EDIT_PREPROCESSING_INITIATED: "editPreprocessingInitiated",
  EDIT_PREVIEW_INTERCHANGE: "handleEnterEdit",
  HANDLE_EDIT_UPSELL: "handleEditUpsell",
  SUBMIT_FORM: "submitform",
  QUICK_TOOL_VERB_CHANGE: "onQuickToolVerbChange",
  FIRST_TIME_EDIT_EVENT: "editEnterSuccessful",
  OPEN_DOCUMENT_IN_DESKTOP: "openDocumentInDesktop",
  CONTEXT_MENU_ITEM_CLICKED: "contextMenuItemClicked",
  FULL_SCREEN_VIEW_ENTRY: "fullScreenViewEntry",
  FULL_SCREEN_VIEW_EXIT: "fullScreenViewExit",
  EDIT_GENAI_TEXT_TRANSFORM: "editGenAITextTransform",
  EDIT_TOOLS_USED: "editToolsUsed",
  DIGSIG_VALIDATION_STATUS_UPDATED: "digSigValidationStatusUpdated",
  WAITING_ON_COMPLETE_PDF_BUFFER: "waitingOnCompletePDFBuffer",
  DOCUMENT_VIEW_THEME_CHANGE: "documentViewThemeChange",
  HANDLE_EDIT_KEYBOARD_SHORTCUT: "handleEditKeyboardShortcut",
  DOCUMENT_VIEW_STATE_UPDATE: "documentViewStateUpdate",
  HANDLE_EDIT_LOCAL_FONT_PRIMING_DIALOG_EVENTS: "handleEditLocalFontPrimingDialogEvents"
});

const privateEventNames = Object.freeze({
  CUSTOMIZE_TOP_BAR: "customizetopbar"
});

const constants = Object.freeze({
  HYPERLINK_TYPE_INTERNAL: "internal",
  HYPERLINK_TYPE_EXTERNAL: "external",
  SELECTED_CONTENT_TEXT: "text"
});

const allowedPageViewModes = Object.freeze({
  [appIdentifiers.FIT_WIDTH]: pageViewModifiers.FIT_WIDTH,
  [appIdentifiers.FIT_PAGE]: pageViewModifiers.FIT_PAGE
});

const allowedDocViewModes = Object.freeze({
  [appIdentifiers.SINGLE_PAGE]: docViewModes.SINGLE_PAGE,
  [appIdentifiers.PAGE_BY_PAGE]: docViewModes.PAGE_BY_PAGE,
  [appIdentifiers.CONTINUOUS]: docViewModes.CONTINUOUS,
  [appIdentifiers.IN_PLACE]: docViewModes.IN_PLACE,
  [appIdentifiers.TWO_COLUMN]: docViewModes.TWO_COLUMN,
  [appIdentifiers.TWO_COLUMN_FIT_WIDTH]: docViewModes.TWO_COLUMN_FIT_WIDTH,
  [appIdentifiers.TWO_COLUMN_FIT_PAGE]: docViewModes.TWO_COLUMN_FIT_PAGE
});

const allowedDocViewModesForApi = Object.freeze({
  [appIdentifiers.SINGLE_PAGE]: docViewModes.SINGLE_PAGE,
  [appIdentifiers.PAGE_BY_PAGE]: docViewModes.PAGE_BY_PAGE,
  [appIdentifiers.CONTINUOUS]: docViewModes.CONTINUOUS,
  [appIdentifiers.TWO_COLUMN]: docViewModes.TWO_COLUMN,
  [appIdentifiers.TWO_COLUMN_FIT_WIDTH]: docViewModes.TWO_COLUMN_FIT_WIDTH,
  [appIdentifiers.TWO_COLUMN_FIT_PAGE]: docViewModes.TWO_COLUMN_FIT_PAGE,
  [appIdentifiers.CLASSIC_VIEW]: docViewModes.CLASSIC_VIEW,
  [appIdentifiers.FIT_ONE_FULL_PAGE]: docViewModes.FIT_ONE_FULL_PAGE,
  [appIdentifiers.FULL_SCREEN]: docViewModes.FULL_SCREEN
});

const lhpFeaturesForDefaultView = Object.freeze({
  [appIdentifiers.THUMBNAILS]: lhpFeatures.THUMBNAIL,
  [appIdentifiers.BOOKMARKS]: lhpFeatures.BOOKMARKS,
  [appIdentifiers.ATTACHMENTS]: lhpFeatures.ATTACHMENTS
});

const explicitlyDisableFeatures = Object.freeze({
  ZOOM: "ZOOM"
});

const allowedEventsToBeTriggeredByOverlay = [
  eventNames.ERROR_OCCURRED,
  eventNames.EDIT_PREPROCESSING_INITIATED,
  eventNames.EDIT_PREVIEW_INTERCHANGE,
  eventNames.FIRST_TIME_EDIT_EVENT,
  eventNames.HANDLE_EDIT_UPSELL,
  eventNames.EDIT_GENAI_TEXT_TRANSFORM,
  eventNames.EDIT_TOOLS_USED,
  eventNames.HANDLE_EDIT_KEYBOARD_SHORTCUT,
  eventNames.HANDLE_EDIT_LOCAL_FONT_PRIMING_DIALOG_EVENTS
];

const excludedMobileMVApis = [apiNames.HUD];

const allowedRHPPositions = [RHP_VERBS_POSITION.TOP, RHP_VERBS_POSITION.BOTTOM];

const validThemes = [flyoutThemes.AUTO, flyoutThemes.DARK, flyoutThemes.LIGHT];

export {
  publicApiNames,
  eventNames,
  privateEventNames,
  constants,
  allowedPageViewModes,
  allowedDocViewModesForApi,
  allowedDocViewModes,
  lhpFeaturesForDefaultView,
  explicitlyDisableFeatures,
  allowedEventsToBeTriggeredByOverlay,
  excludedMobileMVApis,
  allowedRHPPositions,
  validThemes
};
